import { SingleExecutionHelper } from '@disy/lib/single-execution-helper';

export interface ApiCallback<T> {
  // last parameter should be of type AbortSignal
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (...args: any[]): Promise<T>;
}

export interface CancelableApiCallback<T> extends ApiCallback<T> {
  cancelCurrent: () => boolean;
}

type BackendAbortController = AbortController & {
  abortOnUnload: boolean | Promise<unknown>;
};

export function frontendSingleExecution<T>(apiCallback: ApiCallback<T>) {
  return singleExecution<T>(apiCallback, () => new AbortController());
}

/**
 * IMPORTANT: YOU SHOULD NOT CALL THIS FUNCTION EVERYTIME YOU WANT TO MAKE A NEW REQUEST, INSTEAD CALL THE RETURNED FUNCTION.
 *
 * Decorate the apiCallback function with abort & singleExecution capabilities.
 * The correct way to use this function is to call it one time in order to decorate your apiCallback,
 * and then always use the returned function.
 *
 * @example
 *   // last parameter of getData should be of type AbortSignal
 *   // function getData (..., abortSignal) { ... }
 *   const singleExecutionGetData = singleExecution(getData);
 *   singleExecutionGetData(param1, param2);
 *   singleExecutionGetData(param1, param2); // aborts the previous getData()
 * @example
 *   // Abort the current execution:
 *   singleExecutionGetData.cancelCurrent();
 * @param apiCallback - The ajax call which we want to decorate with singleExecution capabilities
 * @param abortControllerProvider - A function that yields an AbortController.
 * @param cancelTimeout - If set it causes request to be canceled after timeout.
 * @return - a function wrapping the apiCallback parameter which will cancel previous requests when used 2+ times.
 * The returned function also has a property function cancelCurrent which allows you to manually cancel a request
 */
export function singleExecution<T>(
  apiCallback: ApiCallback<T>,
  abortControllerProvider: () => AbortController | BackendAbortController,
  cancelTimeout = 0,
): CancelableApiCallback<T> {
  const singleExecutionHelper = new SingleExecutionHelper();
  const singleExecutionFn = function (this: unknown, ...args: unknown[]) {
    const abortController = abortControllerProvider();
    let isFinished = false;
    singleExecutionHelper.submit(abortController, () => isFinished);
    const timeoutCancelId =
      cancelTimeout ? setTimeout(() => singleExecutionHelper.cancelCurrent(), cancelTimeout) : undefined;
    const promise = apiCallback.apply(this, [...args, abortController.signal]);
    const isBackendAbort = (abortController as BackendAbortController).abortOnUnload !== undefined;
    if (isBackendAbort) {
      (abortController as BackendAbortController).abortOnUnload = promise;
    }

    return promise.finally(() => {
      if (timeoutCancelId) {
        clearTimeout(timeoutCancelId);
      }
      isFinished = true;
    });
  };
  return Object.assign(singleExecutionFn, { cancelCurrent: () => singleExecutionHelper.cancelCurrent() });
}
